import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, MapPin, Phone, CheckCircle, XCircle, FileText, Camera, Siren } from 'lucide-react';
import apiService from '../apiService';
import { useLiveRefresh } from '../liveRefresh';
import Img from '../components/Img';

/**
 * Centre de sécurité — alertes SOS & veille (temps réel, cloisonné ville),
 * dossier d'enquête imprimable (à remettre à la police), revue des selfies
 * de vérification chauffeur, numéros d'urgence par ville.
 * ⚠ apiService.get admin IGNORE l'objet params → query string dans l'URL.
 */

const fmtDT = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const KIND_CFG = {
  sos: { label: '🆘 SOS', bg: '#FEE2E2', color: '#B91C1C' },
  watch: { label: '🟠 Veille', bg: '#FEF3C7', color: '#B45309' },
};
const REASON_FR = { stopped: 'arrêt prolongé', signal_lost: 'signal perdu', wrong_way: 'mauvaise direction' };
const STATUS_FR = { checkin_sent: 'Vérification en cours', active: 'ACTIVE', resolved: 'Résolue', dismissed: 'Classée' };

export default function SecurityCenterPage() {
  const [tab, setTab] = useState('alerts'); // alerts | selfies | numbers
  const [alerts, setAlerts] = useState([]);
  const [alertFilter, setAlertFilter] = useState('active');
  const [selfies, setSelfies] = useState([]);
  const [cities, setCities] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState('');

  const loadAlerts = useCallback(async (silent = false) => {
    try {
      const r = await apiService.get(`/safety/admin/alerts?status=${alertFilter}`, { userType: 'admin', silent401: silent });
      setAlerts(r.alerts || []);
    } catch (_) {}
  }, [alertFilter]);

  const loadSelfies = useCallback(async () => {
    try {
      const r = await apiService.get('/safety/admin/selfies?status=submitted', { userType: 'admin' });
      setSelfies(r.checks || []);
    } catch (_) {}
  }, []);

  const loadCities = useCallback(async () => {
    try {
      const r = await apiService.get('/safety/admin/emergency-numbers', { userType: 'admin' });
      setCities(r.cities || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadAlerts(); loadSelfies(); loadCities(); }, [loadAlerts, loadSelfies, loadCities]);
  useLiveRefresh(() => { loadAlerts(true); loadSelfies(); }, ['safety_*']);
  // Rafraîchissement discret des alertes (30 s)
  useEffect(() => {
    const id = setInterval(() => loadAlerts(true), 30000);
    return () => clearInterval(id);
  }, [loadAlerts]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 3500); };

  const closeAlert = async (a, status) => {
    const note = window.prompt(status === 'resolved' ? 'Note de résolution (facultatif) :' : 'Motif de classement (facultatif) :') ?? null;
    if (note === null && !window.confirm('Continuer sans note ?')) return;
    setBusy(a.alert_id);
    try {
      await apiService.patch(`/safety/admin/alerts/${a.alert_id}`, { status, note: note || '' }, { userType: 'admin' });
      flash(status === 'resolved' ? 'Alerte résolue ✅' : 'Alerte classée');
      loadAlerts();
    } catch (e) { flash(e.message || 'Erreur'); } finally { setBusy(null); }
  };

  // Dossier d'enquête imprimable (nouvelle fenêtre → Imprimer / PDF)
  const openReport = async (a) => {
    setBusy(a.alert_id);
    try {
      const r = await apiService.get(`/safety/admin/alerts/${a.alert_id}/report`, { userType: 'admin' });
      const { alert, driver, client, ride, trace } = r;
      const pts = trace?.points || [];
      const step = Math.max(1, Math.floor(pts.length / 150));
      const sampled = pts.filter((_, i) => i % step === 0 || i === pts.length - 1);
      const row = (l, v) => `<tr><td style="padding:4px 10px;color:#666;white-space:nowrap">${l}</td><td style="padding:4px 10px;font-weight:600">${v ?? '—'}</td></tr>`;
      const contacts = (list) => (list || []).map(c => `${c.name} (${c.relation || 'proche'}) — ${c.phone}`).join('<br/>') || '—';
      const w = window.open('', '_blank');
      w.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"/><title>Dossier d'enquête Mbiyo — ${alert.alert_id.slice(0, 8).toUpperCase()}</title>
<style>body{font-family:Arial,sans-serif;margin:28px;color:#111}h1{font-size:20px}h2{font-size:15px;margin:22px 0 6px;border-bottom:2px solid #F54119;padding-bottom:3px}table{border-collapse:collapse;font-size:13px}td{border:1px solid #e5e5e5}.small{font-size:11px;color:#777}@media print{.noprint{display:none}}</style></head><body>
<button class="noprint" onclick="window.print()" style="padding:9px 18px;background:#F54119;color:#fff;border:none;border-radius:8px;font-weight:700;cursor:pointer">🖨️ Imprimer / Enregistrer en PDF</button>
<h1>🛡️ Dossier d'enquête — Mbiyo</h1>
<p class="small">Alerte n° ${alert.alert_id} · générée le ${new Date().toLocaleString('fr-FR')} · document confidentiel destiné aux autorités.</p>
<h2>Alerte</h2><table>
${row('Type', (alert.kind === 'sos' ? 'SOS (déclenchement manuel)' : `Veille automatique — ${REASON_FR[alert.watch_reason] || alert.watch_reason}`))}
${row('Déclenchée par', alert.source)}${row('Date/heure', new Date(alert.created_at).toLocaleString('fr-FR'))}
${row('Ville', alert.city_slug)}${row('Statut', STATUS_FR[alert.status] || alert.status)}
${row('Dernière position connue', alert.last_location?.lat != null ? `${alert.last_location.lat}, ${alert.last_location.lng} (${new Date(alert.last_location.at).toLocaleString('fr-FR')}) — <a href="https://maps.google.com/?q=${alert.last_location.lat},${alert.last_location.lng}">voir sur la carte</a>` : '—')}
</table>
<h2>Chauffeur</h2><table>
${row('Nom', driver?.name)}${row('ID public', driver?.public_id)}${row('Téléphone', driver?.phone)}${row('Email', driver?.email)}
${row('N° permis', driver?.license_number)}${row("N° pièce d’identité", driver?.id_number)}
${row('Véhicule', driver?.vehicle?.plate ? `${driver.vehicle.brand || ''} ${driver.vehicle.model || ''} ${driver.vehicle.color || ''} — plaque ${driver.vehicle.plate}` : '—')}
${row('Statut compte', driver?.status)}${row('Inscrit le', driver?.created_at ? new Date(driver.created_at).toLocaleDateString('fr-FR') : '—')}
${row("Contacts d’urgence", contacts(driver?.emergency_contacts))}
</table>
${driver?.photo ? `<p><img src="${driver.photo}" alt="photo chauffeur" style="width:110px;border-radius:8px"/>${driver.id_document ? ` <img src="${driver.id_document}" alt="pièce d'identité" style="height:110px;border-radius:8px"/>` : ''}</p>` : ''}
<h2>Client</h2><table>
${row('Nom', client?.name)}${row('ID public', client?.public_id)}${row('Téléphone', client?.phone)}${row('Email', client?.email)}
${row("Contacts d’urgence", contacts(client?.emergency_contacts))}
</table>
<h2>Course</h2><table>
${row('N° course', ride?.ride_id)}${row('Statut', ride?.status)}
${row('Départ', ride?.pickup?.address)}${row('Destination', ride?.dropoff?.address)}
${row('Demandée le', ride?.created_at ? new Date(ride.created_at).toLocaleString('fr-FR') : '—')}
${row('Acceptée le', ride?.accepted_at ? new Date(ride.accepted_at).toLocaleString('fr-FR') : '—')}
${row('Démarrée le', ride?.started_at ? new Date(ride.started_at).toLocaleString('fr-FR') : '—')}
</table>
<h2>Trace GPS (${pts.length} points${trace?.pinned ? ' — conservée sans limite' : ''})</h2>
${pts.length ? `<p class="small">Échantillon ${sampled.length} / ${pts.length} points, horodatés. Premier : ${new Date(pts[0].t).toLocaleString('fr-FR')} — dernier : ${new Date(pts[pts.length - 1].t).toLocaleString('fr-FR')}.</p>
<table><tr><td style="padding:4px 10px;font-weight:700">Heure</td><td style="padding:4px 10px;font-weight:700">Latitude</td><td style="padding:4px 10px;font-weight:700">Longitude</td><td style="padding:4px 10px;font-weight:700">Carte</td></tr>
${sampled.map(p => `<tr><td style="padding:3px 10px">${new Date(p.t).toLocaleTimeString('fr-FR')}</td><td style="padding:3px 10px">${p.lat.toFixed(6)}</td><td style="padding:3px 10px">${p.lng.toFixed(6)}</td><td style="padding:3px 10px"><a href="https://maps.google.com/?q=${p.lat},${p.lng}">ouvrir</a></td></tr>`).join('')}
</table>` : '<p>Aucune trace enregistrée pour cette course.</p>'}
<p class="small">Document généré par la plateforme Mbiyo — contact : info@mbiyo.app.</p>
</body></html>`);
      w.document.close();
    } catch (e) { flash(e.message || 'Erreur dossier'); } finally { setBusy(null); }
  };

  const reviewSelfie = async (c, action) => {
    const note = action === 'reject' ? (window.prompt('Motif du refus (envoyé au chauffeur) :') || '') : '';
    setBusy(c.check_id);
    try {
      await apiService.patch(`/safety/admin/selfies/${c.check_id}`, { action, note }, { userType: 'admin' });
      flash(action === 'approve' ? 'Selfie approuvé ✅' : 'Selfie refusé — le chauffeur devra recommencer');
      loadSelfies();
    } catch (e) { flash(e.message || 'Erreur'); } finally { setBusy(null); }
  };

  const saveNumber = async (city, phone) => {
    setBusy(city.slug);
    try {
      await apiService.put('/safety/admin/emergency-number', { city_slug: city.slug, phone }, { userType: 'admin' });
      flash(`Numéro d'urgence de ${city.name} enregistré`);
      loadCities();
    } catch (e) { flash(e.message || 'Erreur'); } finally { setBusy(null); }
  };

  const card = { background: 'white', borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '0.75rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' };
  const btn = (bg, color) => ({ padding: '7px 13px', background: bg, color, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' });
  const activeCount = alerts.filter(a => ['active', 'checkin_sent'].includes(a.status)).length;

  return (
    <div className="dashboard-content">
      <h1 className="page-title">🛡️ Centre de sécurité</h1>
      {msg && <div style={{ padding: '10px 16px', borderRadius: 10, marginBottom: '1rem', background: '#ecfdf5', color: '#059669', fontWeight: 600 }}>{msg}</div>}

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[['alerts', <><Siren size={15} /> Alertes {activeCount > 0 && <b style={{ background: '#DC2626', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: '0.75rem' }}>{activeCount}</b>}</>],
          ['selfies', <><Camera size={15} /> Selfies à vérifier {selfies.length > 0 && <b style={{ background: '#D97706', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: '0.75rem' }}>{selfies.length}</b>}</>],
          ['numbers', <><Phone size={15} /> Numéros d'urgence</>]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, border: '1px solid #DCE0E6', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', background: tab === k ? '#1C1C1C' : 'white', color: tab === k ? 'white' : '#5C636E' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Alertes ── */}
      {tab === 'alerts' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
            {[['active', 'En cours'], ['resolved', 'Résolues'], ['dismissed', 'Classées']].map(([k, l]) => (
              <button key={k} onClick={() => setAlertFilter(k)} style={{ padding: '6px 14px', borderRadius: 16, border: '1px solid #DCE0E6', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', background: alertFilter === k ? '#FEE9E2' : 'white', color: alertFilter === k ? '#C9350F' : '#5C636E' }}>{l}</button>
            ))}
          </div>
          {alerts.length === 0 && <div style={{ ...card, textAlign: 'center', color: '#9AA0A8', padding: '2.5rem' }}><ShieldAlert size={36} style={{ opacity: .4 }} /><p style={{ margin: '0.5rem 0 0' }}>Aucune alerte {alertFilter === 'active' ? 'en cours — tout va bien' : ''}.</p></div>}
          {alerts.map(a => {
            const k = KIND_CFG[a.kind] || KIND_CFG.watch;
            return (
              <div key={a.alert_id} style={{ ...card, borderLeft: `5px solid ${a.kind === 'sos' ? '#DC2626' : '#D97706'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ background: k.bg, color: k.color, borderRadius: 8, padding: '3px 10px', fontWeight: 800, fontSize: '0.8rem' }}>
                    {k.label}{a.watch_reason ? ` · ${REASON_FR[a.watch_reason]}` : ''}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: '0.85rem', color: a.status === 'active' ? '#DC2626' : '#5C636E' }}>{STATUS_FR[a.status]}</span>
                  <span style={{ color: '#9AA0A8', fontSize: '0.8rem' }}>{fmtDT(a.created_at)} · {a.city_slug || '—'} · source : {a.source}</span>
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', margin: '0.6rem 0', flexWrap: 'wrap', fontSize: '0.88rem' }}>
                  <div><b>Client :</b> {a.client?.name || '—'} {a.client?.phone && <a href={`tel:${a.client.phone}`}>({a.client.phone})</a>}</div>
                  <div><b>Chauffeur :</b> {a.driver?.name || '—'} {a.driver?.phone && <a href={`tel:${a.driver.phone}`}>({a.driver.phone})</a>} {a.driver?.plate && <span style={{ background: '#1C1C1C', color: '#fff', borderRadius: 6, padding: '1px 7px', fontWeight: 700, fontSize: '0.78rem' }}>{a.driver.plate}</span>}</div>
                  {a.last_location?.lat != null && (
                    <a href={`https://maps.google.com/?q=${a.last_location.lat},${a.last_location.lng}`} target="_blank" rel="noreferrer" style={{ color: '#F54119', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={14} /> Dernière position ({fmtDT(a.last_location.at)})
                    </a>
                  )}
                </div>
                {a.resolution_note && <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: '#5C636E' }}>📝 {a.resolution_note} <i>({a.resolved_by})</i></p>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button style={btn('#EFF6FF', '#1D4ED8')} disabled={busy === a.alert_id} onClick={() => openReport(a)}><FileText size={13} style={{ verticalAlign: '-2px' }} /> Dossier d'enquête</button>
                  {['active', 'checkin_sent'].includes(a.status) && (
                    <>
                      <button style={btn('#ECFDF5', '#047857')} disabled={busy === a.alert_id} onClick={() => closeAlert(a, 'resolved')}><CheckCircle size={13} style={{ verticalAlign: '-2px' }} /> Résoudre</button>
                      <button style={btn('#F4F5F7', '#5C636E')} disabled={busy === a.alert_id} onClick={() => closeAlert(a, 'dismissed')}><XCircle size={13} style={{ verticalAlign: '-2px' }} /> Fausse alerte</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── Selfies ── */}
      {tab === 'selfies' && (
        <>
          <SelfieSettingsCard onFlash={flash} />
          {selfies.length === 0 && <div style={{ ...card, textAlign: 'center', color: '#9AA0A8', padding: '2.5rem' }}>Aucun selfie en attente de vérification.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '0.75rem' }}>
            {selfies.map(c => (
              <div key={c.check_id} style={card}>
                <div style={{ display: 'flex', gap: 10 }}>
                  {c.photo_url && <a href={c.photo_url} target="_blank" rel="noreferrer"><Img src={c.photo_url} alt="selfie" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 10 }} /></a>}
                  {c.driver?.photo
                    ? <a href={c.driver.photo} target="_blank" rel="noreferrer"><Img src={c.driver.photo} alt="dossier" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 10, border: '2px solid #F54119' }} title="Photo du dossier" /></a>
                    : <div style={{ width: 90, height: 90, borderRadius: 10, border: '2px dashed #F54119', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#9AA0A8', textAlign: 'center' }} title="Aucune photo de profil dans le dossier">Pas de photo<br />au dossier</div>}
                </div>
                <p style={{ margin: '0.6rem 0 0.2rem', fontWeight: 700 }}>{c.driver?.name || c.driver_id}</p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#9AA0A8' }}>{c.driver?.public_id} · {c.driver?.city_slug} · envoyé {fmtDT(c.submitted_at)}</p>
                <p style={{ margin: '0.2rem 0 0.6rem', fontSize: '0.75rem', color: '#9AA0A8' }}>Gauche : selfie reçu · droite (bord feu) : photo du dossier</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btn('#ECFDF5', '#047857')} disabled={busy === c.check_id} onClick={() => reviewSelfie(c, 'approve')}>✓ C'est bien lui</button>
                  <button style={btn('#FEE2E2', '#B91C1C')} disabled={busy === c.check_id} onClick={() => reviewSelfie(c, 'reject')}>✗ Refuser</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Numéros d'urgence ── */}
      {tab === 'numbers' && (
        <div style={{ ...card, maxWidth: 560 }}>
          <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#5C636E' }}>
            Numéro composé quand un client ou un chauffeur appuie sur SOS dans cette ville (police locale,
            ligne d'urgence Mbiyo…). Vide = le bouton d'appel est masqué, l'alerte dashboard part quand même.
          </p>
          {cities.map(c => (
            <div key={c.slug} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.5rem 0', borderBottom: '1px solid #F4F5F7' }}>
              <span style={{ flex: 1, fontWeight: 600 }}>{c.name}</span>
              <input defaultValue={c.emergency_phone || ''} placeholder="+243…" id={`num-${c.slug}`}
                style={{ width: 170, padding: '8px 12px', border: '1px solid #DCE0E6', borderRadius: 8 }} />
              <button style={btn('#FEE9E2', '#C9350F')} disabled={busy === c.slug}
                onClick={() => saveNumber(c, document.getElementById(`num-${c.slug}`).value)}>Enregistrer</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Réglages du selfie de vérification (global, toutes villes) — décisions du 2026-10-22 :
 * interrupteur Active / En pause, fréquence (jours), période de grâce des nouveaux chauffeurs.
 * Le serveur ne met jamais un chauffeur hors ligne : rappel push, selfie exigé au passage en ligne.
 * Modification réservée aux admins « toutes villes » (le serveur refuse les autres).
 */
function SelfieSettingsCard({ onFlash }) {
  const [data, setData] = useState(null);
  const [freq, setFreq] = useState('');
  const [grace, setGrace] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiService.get('/safety/admin/selfie-settings', { userType: 'admin' });
      if (r?.success) {
        setData(r);
        setFreq(String(r.settings.frequency_days));
        setGrace(String(r.settings.grace_days));
      }
    } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, okText) => {
    setSaving(true); setError('');
    try {
      const r = await apiService.put('/safety/admin/selfie-settings', patch, { userType: 'admin' });
      if (!r?.success) throw new Error(r?.error || 'Enregistrement impossible');
      let t = okText;
      if (r.released) t += ` — ${r.released} chauffeur(s) débloqué(s)`;
      if (r.rescheduled) t += ` — nouvelles échéances pour ${r.rescheduled} chauffeur(s)`;
      onFlash && onFlash(t);
      await load();
    } catch (e) {
      setError(e?.error || e?.message || 'Enregistrement impossible');
    } finally { setSaving(false); }
  };

  if (!data) return null;
  const on = data.settings.enabled;
  const toggle = () => {
    const question = on
      ? `Mettre la vérification par selfie en PAUSE ?\n\nPlus aucune demande ni aucun blocage. ${data.pending_required || 0} demande(s) en attente seront levées : ces chauffeurs pourront repasser en ligne tout de suite.`
      : 'RÉACTIVER la vérification par selfie ?\n\nChaque chauffeur reçoit une nouvelle échéance au hasard (après sa période de grâce s\'il est nouveau). Personne n\'est bloqué aujourd\'hui.';
    if (!window.confirm(question)) return;
    save({ enabled: !on }, on ? 'Vérification par selfie mise en pause' : 'Vérification par selfie réactivée');
  };
  const dirty = String(data.settings.frequency_days) !== freq || String(data.settings.grace_days) !== grace;
  const box = { background: 'white', borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '0.75rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderLeft: `4px solid ${on ? '#10B981' : '#9AA0A8'}` };
  const field = { width: 90, padding: '7px 10px', border: '1px solid #DCE0E6', borderRadius: 8, fontSize: '0.9rem' };

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1rem' }}>📸 Vérification par selfie — {on ? <span style={{ color: '#047857' }}>active</span> : <span style={{ color: '#5C636E' }}>en pause</span>}</div>
          <div style={{ fontSize: '0.82rem', color: '#5C636E', marginTop: 2, maxWidth: 640 }}>
            {on
              ? 'À l\'échéance, le chauffeur reçoit un rappel ; le selfie lui est demandé quand il passe lui-même en ligne. Il n\'est jamais mis hors ligne par le système.'
              : 'Aucune demande de selfie, aucun blocage. Les chauffeurs passent en ligne librement.'}
            {on && data.pending_required > 0 && <> {data.pending_required} chauffeur(s) ont un selfie à faire.</>}
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          style={{ background: on ? '#F4F5F7' : '#10B981', color: on ? '#1E2229' : '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontWeight: 700, cursor: 'pointer' }}
        >
          {on ? '⏸ Mettre en pause' : '▶ Réactiver'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
        <label style={{ fontSize: '0.8rem', color: '#5C636E', fontWeight: 600 }}>
          Un selfie tous les (jours)<br />
          <input type="number" min={1} max={90} value={freq} onChange={e => setFreq(e.target.value)} style={field} />
        </label>
        <label style={{ fontSize: '0.8rem', color: '#5C636E', fontWeight: 600 }}>
          Grâce des nouveaux chauffeurs (jours)<br />
          <input type="number" min={0} max={180} value={grace} onChange={e => setGrace(e.target.value)} style={field} />
        </label>
        <button
          onClick={() => save({ frequency_days: Number(freq), grace_days: Number(grace) }, 'Réglages du selfie enregistrés')}
          disabled={saving || !dirty}
          style={{ background: dirty ? '#7C3AED' : '#E9EBEF', color: dirty ? '#fff' : '#9AA0A8', border: 'none', borderRadius: 10, padding: '8px 14px', fontWeight: 700, cursor: dirty ? 'pointer' : 'default' }}
        >
          Enregistrer
        </button>
        <span style={{ fontSize: '0.75rem', color: '#9AA0A8', maxWidth: 360 }}>
          Défauts : {data.defaults?.frequency_days ?? 7} j et {data.defaults?.grace_days ?? 14} j. La grâce compte depuis la validation du compte. La fréquence s'applique aux prochaines échéances.
        </span>
      </div>
      {error && <div style={{ marginTop: 8, color: '#B91C1C', fontSize: '0.82rem', fontWeight: 600 }}>{error}</div>}
    </div>
  );
}
